import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { calculateGridTemplateColumns } from 'components/DesignSystems/FilterHeader/calculateGridTemplateColumns';
import { AssignmentListRow } from './AssignmentContainer';
import FilterCardHeader from 'components/DesignSystems/FilterHeader/FilterCardHeader';
import AssignmentCardWorkAssignment from './AssignmentCardWorkAssignment';
import {
  useApproveWorkAssignmentListAttachment,
  useDenyWorkAssignmentListAttachment,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import { SectionChiefInfo } from './useSectionChiefResolver';
import AssignmentCardResourceNeeds from './AssignmentCardResourceNeeds';
import AssignmentCardResourceSectionView from './AssignmentCardResourceSectionView';
import CommunicationsDisplay from './CommunicationsDisplay';
import { NULL_VALUE } from 'constants/displayConstants';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useState } from 'react';
import ChangeLogDialog from './ChangeLogDialog';
import AssignmentCardSpecialInstructions from './AssignmentCardSpecialInstructions';
import dayjs from 'dayjs';

type AssignmentCardViewProps = {
  item: AssignmentListRow;
  columns: string[];
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  setIsEditing: (isEditing: boolean) => void;
  sectionChief?: SectionChiefInfo;
  isCurrentUserReviewer: boolean;
  readOnly?: boolean;
};

const AssignmentCardView = ({
  columns,
  item,
  expanded,
  setExpanded,
  setIsEditing,
  sectionChief,
  isCurrentUserReviewer,
  readOnly = false,
}: AssignmentCardViewProps) => {
  // in order for these to work we need to deterine the walaId
  const { mutate: approve } = useApproveWorkAssignmentListAttachment();
  const { mutate: deny } = useDenyWorkAssignmentListAttachment();
  const { mutate: update } = useUpdateWorkAssignmentListAttachment();
  const { exportWorkAssignmentListAttachment } = useIcsFormPdfPreview();

  const gridTemplateColumns = calculateGridTemplateColumns(columns.length);

  const headerDisplayColumns = columns.map((column) => {
    const value = item[column as keyof AssignmentListRow];

    if (value === null || value === undefined) {
      return NULL_VALUE;
    }

    return String(value) || NULL_VALUE;
  });

  const assign = (walaId: string) => {
    update({ walaId, proposedStatus: 'in_progress' });
  };

  const setComplete = (walaId: string) => {
    update({ walaId, proposedStatus: 'completed' });
  };

  const setIncomplete = (walaId: string) => {
    update({ walaId, proposedStatus: 'incomplete' });
  };

  const [showChangeLog, setShowChangeLog] = useState(false);

  const latestWorkAssignmentChange =
    Array.isArray(item.workAssignments) && item.workAssignments.length > 0
      ? item.workAssignments.reduce((acc, assignment) => {
          const latestDate = dayjs(assignment.timestamp);
          const currentDate = dayjs(acc);
          return currentDate > latestDate ? acc : assignment.timestamp;
        }, item.workAssignments[0].timestamp)
      : null;

  return (
    <>
      <div onClick={() => setExpanded(!expanded)} role="button">
        <FilterCardHeader gridTemplateColumns={gridTemplateColumns}>
          <div>
            {!expanded ? (
              <SharedIcon iconName="keyboard_arrow_right" size="sm" />
            ) : (
              <SharedIcon iconName="keyboard_arrow_down" size="sm" />
            )}
          </div>
          {headerDisplayColumns.map((cell, idx) => (
            <div key={`${columns[idx]}-${idx}`}>{cell}</div>
          ))}
          <div>
            <StylishNewButton
              className="button--icon me-2"
              title="Preview ICS-204"
              onClick={(e) => {
                e.stopPropagation();
                void exportWorkAssignmentListAttachment(item.walaId as string);
              }}
              disabled={item.walaId === null}
            >
              <SharedIcon iconName="news" />
            </StylishNewButton>
            <StylishNewButton
              className="button--icon me-2"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <SharedIcon iconName="map" />
            </StylishNewButton>
            {!readOnly && (
              <StylishNewButton
                className={`button--icon ${item.actualStatus === 'awaiting_review' ? 'opacity-50' : ''}`}
                disabled={item.workGroup === ''}
                onClick={(e) => {
                  if (item.actualStatus !== 'awaiting_review') {
                    setIsEditing(true);
                    e.stopPropagation();
                  } else {
                    e.stopPropagation();
                  }
                }}
              >
                <SharedIcon iconName="edit" />
              </StylishNewButton>
            )}
          </div>
        </FilterCardHeader>
      </div>
      {expanded && (
        <div className="filter-card-content">
          {item.actualStatus === 'awaiting_review' && (
            <div className="alert alert-info m-3">
              This work assignment list attachment is awaiting review. It cannot
              be edited until it is reviewed.
            </div>
          )}
          <div className="filter-card-content-grid mt-4 p-4">
            <div title="Section Chief">{`${sectionChief?.positionName}: ${sectionChief?.personName || NULL_VALUE}`}</div>
            <div title="Branch Director">{`Branch Director: ${item.branchDirector || NULL_VALUE}`}</div>
            <div title="Division/Group Supervisor">
              {`Division/Group Supervisior: ${item.divisionGroupSupervisor || NULL_VALUE}`}
            </div>
          </div>
          {!!item.workGroupResourceNeeds &&
            item.workGroupResourceNeeds.length > 0 && (
              <>
                <div className="mb-2">
                  <span className="fw-bold">
                    Work Assignment Resource Needs
                  </span>{' '}
                  <span className="fst-italic px-3">
                    updated on{' '}
                    {latestWorkAssignmentChange
                      ? dayjs(latestWorkAssignmentChange).format('MM/DD/YYYY')
                      : NULL_VALUE}
                  </span>
                </div>
                <div className="mx-3">
                  <AssignmentCardResourceNeeds
                    workGroupResourceNeeds={item.workGroupResourceNeeds}
                  />
                </div>
              </>
            )}
          <div>
            <AssignmentCardResourceSectionView
              plannedResources={item.plannedResources}
              actualStatus={item.actualStatus}
              walaId={item.walaId}
            />
          </div>
          {Array.isArray(item.workAssignments) &&
          item.workAssignments.length > 0 ? (
            <>
              <div className="mt-3" title="Work Assignments">
                <div className="fw-bold mb-2">Work Assignments:</div>
                {item.workAssignments.map((wa, idx: number) => {
                  return (
                    <AssignmentCardWorkAssignment
                      key={`work-assignment-${idx}-${wa.id}`}
                      workAssignment={wa}
                    />
                  );
                })}
              </div>
              <div className="mt-3" title="Special Instructions">
                <div className="fw-bold mb-2">Special Instructions:</div>
                <AssignmentCardSpecialInstructions
                  specialInstructions={item.specialInstructions || NULL_VALUE}
                />
              </div>
              <CommunicationsDisplay
                workGroupId={item.workGroupId}
                medicalCommunication={item.medicalCommunication}
                evacuationCommunication={item.evacuationCommunication}
                otherCommunication={item.otherCommunication}
              />
            </>
          ) : (
            <div title="Work Assignments">{`Work Assignments: ${
              (item as any).workAssignments ?? NULL_VALUE
            }`}</div>
          )}
          <div className="d-flex align-items-center justify-content-start flex-row flex-wrap gap-2">
            {isCurrentUserReviewer &&
              item.actualStatus === 'awaiting_review' &&
              item.walaId !== null && (
                <>
                  <StylishNewButton
                    onClick={() => approve(item.walaId as string)}
                  >
                    Approve Complete
                  </StylishNewButton>
                  <StylishNewButton
                    type="button"
                    className="border border-neutral-500 bg-transparent"
                    onClick={() => deny(item.walaId as string)}
                  >
                    Report Incomplete
                  </StylishNewButton>
                </>
              )}

            {isCurrentUserReviewer &&
              item.actualStatus === 'draft' &&
              item.walaId !== null && (
                <StylishNewButton onClick={() => assign(item.walaId as string)}>
                  Assign to work group
                </StylishNewButton>
              )}

            {item.actualStatus === 'in_progress' && item.walaId !== null && (
              <>
                <StylishNewButton
                  onClick={() => setComplete(item.walaId as string)}
                >
                  Submit Completion
                </StylishNewButton>
                <StylishNewButton
                  type="button"
                  className="border border-neutral-500 bg-transparent"
                  onClick={() => setIncomplete(item.walaId as string)}
                >
                  Report Inability to Complete
                </StylishNewButton>
              </>
            )}

            <StylishNewButton
              onClick={() => setShowChangeLog(true)}
              className="border border-neutral-500 bg-transparent"
            >
              Show Change Log
            </StylishNewButton>
          </div>
        </div>
      )}
      {showChangeLog && (
        <ChangeLogDialog
          onClose={() => setShowChangeLog(false)}
          walaId={item.walaId as string}
        />
      )}
    </>
  );
};

export default AssignmentCardView;
